import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Account } from '../shared/account.model';
import { map } from 'rxjs/operators';

export interface IAccount {
  id?: string;
  amount?: number;
  supplierName?: string;
  supplierPhone?: string;
  paidBy?: IUser;
  paidOn?: Date;
  processedOn?: Date;
  createdOn?: Date;
  createdBy?: IUser;
  modifiedOn?: Date;
  modifiedBy?: IUser;
  notes?: string;
  category?: ICategory;
  archivedOn?: Date;
  images?: string[];
}

export interface ICategory {
  id?: string;
  name?: string;
  color?: string;
}

export interface IUser {
  id?: string;
  name?: string;
}

export interface IActivity {
  id?: string;
  action?: 0 | 1 | 2 | 3;
  account?: IAccount;
  createdOn?: Date;
  createdBy?: IUser;
  modifiedOn?: Date;
  modifiedBy?: IUser;
}

export interface IRecentSupplier {
  id?: string;
  supplierName?: string;
  supplierPhone?: string;
  category?: ICategory;
  createdOn?: Date;
  createdBy?: IUser;
  modifiedOn?: Date;
  modifiedBy?: IUser;
}

export interface IRetrieveMultipleResponse<T> {
  entities: T[];
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  constructor(private http: HttpClient) { }

  whoAmI() {
    const url = `${environment.accountService.apiUrl}/whoami`;
    return this.http.get<IUser>(url).toPromise();
  }

  async archiveAccounts(offset?: number): Promise<any> {
    const url = `${environment.accountService.apiUrl}/archiveaccounts`;
    return this.http.post(url, {offset}).toPromise();
  }

  async retrieveAllAccounts(month?: string): Promise<Account[]> {
    const url = [environment.accountService.apiUrl, 'accounts', month].filter(v => v).join('/');
    const response = await (this.http.get(url).toPromise() as Promise<IRetrieveMultipleResponse<IAccount>>);
    return response.entities.sort((a, b) => a.paidOn && b.paidOn && a.paidOn < b.paidOn ? 1 : -1).map(x => new Account(x));
  }

  retrieveAccount(id: string, month?: string) {
    const url = [environment.accountService.apiUrl, 'accounts', month, id].filter(v => v).join('/');
    return this.http.get<IAccount>(url);
  }

  createAccount(entity: IAccount) {
    const url = `${environment.accountService.apiUrl}/accounts`;
    return this.http.post(url, entity);
  }

  updateAccount(id: string, entity: IAccount) {
    const url = `${environment.accountService.apiUrl}/accounts/${id}`;
    return this.http.patch(url, entity);
  }

  deleteAccount(id: string) {
    const url = `${environment.accountService.apiUrl}/accounts/${id}`;
    return this.http.delete(url);
  }

  retrieveAllUsers() {
    const url = `${environment.accountService.apiUrl}/users`;
    return this.http.get<IRetrieveMultipleResponse<IUser>>(url);
  }

  retrieveAllCategories() {
    const url = `${environment.accountService.apiUrl}/categories`;
    return this.http.get<IRetrieveMultipleResponse<ICategory>>(url);
  }

  retrieveAllActivities(token?: string | null) {
    const url = `${environment.accountService.apiUrl}/activities`;
    const parameters = token ? `?token=${token}` : '';
    return this.http.get<IRetrieveMultipleResponse<IActivity>>(url + parameters);
  }

  retrieveRecentSuppliers() {
    const url = `${environment.accountService.apiUrl}/recentsuppliers`;
    return this.http.get<IRetrieveMultipleResponse<IRecentSupplier>>(url);
  }
}
